import { AddRounded, MoreVertOutlined, SearchSharp } from "@mui/icons-material";
import { DeleteSharp, EditSharp } from "@mui/icons-material";
import {
  Box,
  Button,
  Checkbox,
  Chip,
  IconButton,
  InputAdornment,
  ListItemIcon,
  ListItemText,
  Menu,
  MenuItem,
  Paper,
  Stack,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TablePagination,
  TableRow,
  TextField,
  Typography,
  useTheme,
} from "@mui/material";
import dayjs from "dayjs";
import React, { useCallback, useEffect, useMemo, useReducer } from "react";
import { useNavigate } from "react-router-dom";

import EmptyState from "../../components/crm/EmptyState";
import PrivilegeForm from "../../components/crm/PrivilegeForm";
import TableLoadingSkeleton, {
  type TableSkeletonColumn,
} from "../../components/crm/TableLoadingSkeleton";
import CustomConfirmDialog from "../../components/CustomConfirmDialog";
import CustomDialog from "../../components/CustomDialog";
import { useBreadcrumbs } from "../../contexts/BreadcrumbContext";
import { useSnackbar } from "../../contexts/SnackbarContext";
import useCrmPrivileges from "../../hooks/useCrmPrivileges";
import {
  createPrivilege,
  deletePrivilege,
  updatePrivilege,
} from "../../services/crm.service";
import {
  Privilege,
  PrivilegeCreateRequest,
  PrivilegeFilters,
  PrivilegeUpdate,
} from "../../types/crm";

type PrivilegeListFilters = Omit<PrivilegeFilters, "status"> & {
  search: string;
  month: string;
  year: string;
  status: "all" | "active" | "draft" | "expired";
  page: number;
  page_size: number;
};

const monthOptions = [
  { label: "All Month", value: "all" },
  { label: "January", value: "01" },
  { label: "February", value: "02" },
  { label: "March", value: "03" },
  { label: "April", value: "04" },
  { label: "May", value: "05" },
  { label: "June", value: "06" },
  { label: "July", value: "07" },
  { label: "August", value: "08" },
  { label: "September", value: "09" },
  { label: "October", value: "10" },
  { label: "November", value: "11" },
  { label: "December", value: "12" },
];

const statusOptions = [
  { label: "All State", value: "all" },
  { label: "Active", value: "active" },
  { label: "Draft", value: "draft" },
  { label: "Expired", value: "expired" },
];

const getPrivilegeStatus = (privilege: Privilege) => {
  const now = dayjs();
  const startDate = dayjs(privilege.start_date);
  const endDate = privilege.end_date ? dayjs(privilege.end_date) : null;

  if (endDate && endDate.isBefore(now, "day")) {
    return "expired" as const;
  }

  if (startDate.isAfter(now, "day")) {
    return "draft" as const;
  }

  return "active" as const;
};

const getStatusChip = (status: "active" | "draft" | "expired") => {
  if (status === "active") {
    return {
      label: "Active",
      sx: {
        color: "#2E7D32",
        bgcolor: "#E8F5E9",
      },
    };
  }

  if (status === "draft") {
    return {
      label: "Draft",
      sx: {
        color: "#6B7280",
        bgcolor: "#F3F4F6",
      },
    };
  }

  return {
    label: "Expired",
    sx: {
      color: "#D32F2F",
      bgcolor: "#FDECEA",
    },
  };
};

const PRIVILEGES_SKELETON_COLUMNS: TableSkeletonColumn[] = [
  { key: "checkbox", isCheckbox: true, width: 74 },
  { key: "actions", width: 80, align: "center" },
  { key: "number", width: 86, align: "center" },
  { key: "privileges_id", width: 153 },
  { key: "category", width: 150 },
  { key: "privilege_name", width: 200 },
  { key: "privilege_details", width: 200 },
  { key: "status", width: 166, align: "center" },
  { key: "points_required", width: 180, align: "center" },
  { key: "start_date", width: 118, align: "center" },
  { key: "end_date", width: 118, align: "center" },
  { key: "created_date", width: 180, align: "center" },
];

export default function CrmPrivileges() {
  const theme = useTheme();
  const navigate = useNavigate();
  const { setBreadcrumbs } = useBreadcrumbs();
  const { showSnackbar } = useSnackbar();
  const currentYear = dayjs().year();

  type State = {
    filters: PrivilegeListFilters;
    dialogOpen: boolean;
    deleteDialogOpen: boolean;
    bulkDeleteDialogOpen: boolean;
    selectedPrivilege: Privilege | null;
    isSubmitting: boolean;
    selectedIds: string[];
    actionAnchorEl: HTMLElement | null;
    actionRow: Privilege | null;
  };

  type Action =
    | { type: "SET_FILTERS"; payload: Partial<PrivilegeListFilters> }
    | { type: "SET_DIALOG_OPEN"; payload: boolean }
    | { type: "SET_DELETE_DIALOG_OPEN"; payload: boolean }
    | { type: "SET_BULK_DELETE_DIALOG_OPEN"; payload: boolean }
    | { type: "SET_SELECTED_PRIVILEGE"; payload: Privilege | null }
    | { type: "SET_IS_SUBMITTING"; payload: boolean }
    | { type: "SET_SELECTED_IDS"; payload: string[] }
    | { type: "SET_ACTION_ANCHOR_EL"; payload: HTMLElement | null }
    | { type: "SET_ACTION_ROW"; payload: Privilege | null }
    | {
        type: "OPEN_ACTION_MENU";
        payload: { anchorEl: HTMLElement; row: Privilege };
      }
    | { type: "CLOSE_DIALOGS" };

  const initialState: State = {
    filters: {
      search: "",
      month: "all",
      year: String(currentYear),
      status: "all",
      page: 0,
      page_size: 10,
    },
    dialogOpen: false,
    deleteDialogOpen: false,
    bulkDeleteDialogOpen: false,
    selectedPrivilege: null,
    isSubmitting: false,
    selectedIds: [],
    actionAnchorEl: null,
    actionRow: null,
  };

  function reducer(state: State, action: Action): State {
    switch (action.type) {
      case "SET_FILTERS":
        return {
          ...state,
          filters: { ...state.filters, ...action.payload },
          selectedIds: [],
        };
      case "SET_DIALOG_OPEN":
        return { ...state, dialogOpen: action.payload };
      case "SET_DELETE_DIALOG_OPEN":
        return { ...state, deleteDialogOpen: action.payload };
      case "SET_BULK_DELETE_DIALOG_OPEN":
        return { ...state, bulkDeleteDialogOpen: action.payload };
      case "SET_SELECTED_PRIVILEGE":
        return { ...state, selectedPrivilege: action.payload };
      case "SET_IS_SUBMITTING":
        return { ...state, isSubmitting: action.payload };
      case "SET_SELECTED_IDS":
        return { ...state, selectedIds: action.payload };
      case "SET_ACTION_ANCHOR_EL":
        return { ...state, actionAnchorEl: action.payload };
      case "SET_ACTION_ROW":
        return { ...state, actionRow: action.payload };
      case "OPEN_ACTION_MENU":
        return {
          ...state,
          actionAnchorEl: action.payload.anchorEl,
          actionRow: action.payload.row,
        };
      case "CLOSE_DIALOGS":
        return { ...state, dialogOpen: false, deleteDialogOpen: false, bulkDeleteDialogOpen: false };
      default:
        return state;
    }
  }

  const [state, dispatch] = useReducer(reducer, initialState);
  const {
    filters,
    dialogOpen,
    deleteDialogOpen,
    bulkDeleteDialogOpen,
    selectedPrivilege,
    isSubmitting,
    selectedIds,
    actionAnchorEl,
    actionRow,
  } = state;

  const yearOptions = useMemo(
    () => [
      { label: "All Year", value: "all" },
      ...Array.from({ length: 8 }, (_, index) => {
        const year = String(currentYear - index);
        return { label: year, value: year };
      }),
    ],
    [currentYear],
  );

  const apiFilters = useMemo(
    () => ({
      ...filters,
      month: filters.month === "all" ? undefined : Number(filters.month),
      year: filters.year === "all" ? undefined : Number(filters.year),
      status: filters.status === "all" ? undefined : filters.status,
    }),
    [filters],
  );

  const {
    data,
    dataLoading: isLoading,
    mutate,
  } = useCrmPrivileges(true, null, apiFilters as PrivilegeFilters);
  const privileges = data?.data || [];
  const totalRecords = data?.total || 0;

  useEffect(() => {
    setBreadcrumbs([{ label: "Privilege List", to: "/crm/privileges" }]);
  }, [setBreadcrumbs]);

  const handleSearchChange = useCallback((value: string) => {
    dispatch({ type: "SET_FILTERS", payload: { search: value, page: 0 } });
  }, []);

  const handleMonthChange = useCallback((value: string) => {
    dispatch({ type: "SET_FILTERS", payload: { month: value, page: 0 } });
  }, []);

  const handleYearChange = useCallback((value: string) => {
    dispatch({ type: "SET_FILTERS", payload: { year: value, page: 0 } });
  }, []);

  const handleStatusChange = useCallback((value: string) => {
    dispatch({
      type: "SET_FILTERS",
      payload: {
        status: value as PrivilegeListFilters["status"],
        page: 0,
      },
    });
  }, []);

  const handlePageChange = useCallback((_: unknown, page: number) => {
    dispatch({ type: "SET_FILTERS", payload: { page } });
  }, []);

  const handlePageSizeChange = useCallback(
    (event: React.ChangeEvent<HTMLInputElement>) => {
      dispatch({
        type: "SET_FILTERS",
        payload: {
          page_size: parseInt(event.target.value, 10),
          page: 0,
        },
      });
    },
    [],
  );

  const handleAddNew = () => {
    navigate("/crm/privileges/create");
  };

  const handleEdit = (privilege: Privilege) => {
    navigate(`/crm/privileges/${privilege.id}/edit`);
  };

  const handleDeleteClick = (privilege: Privilege) => {
    dispatch({ type: "SET_SELECTED_PRIVILEGE", payload: privilege });
    dispatch({ type: "SET_DELETE_DIALOG_OPEN", payload: true });
  };

  const handleFormSubmit = async (
    formData: PrivilegeCreateRequest | PrivilegeUpdate,
  ) => {
    dispatch({ type: "SET_IS_SUBMITTING", payload: true });
    try {
      if (selectedPrivilege) {
        await updatePrivilege(
          selectedPrivilege.id,
          formData as PrivilegeUpdate,
        );
      } else {
        await createPrivilege(formData as PrivilegeCreateRequest);
      }
      dispatch({ type: "SET_DIALOG_OPEN", payload: false });
      mutate();
      showSnackbar(
        selectedPrivilege ? "Privilege updated successfully" : "Privilege created successfully",
        "success",
      );
    } catch (error) {
      console.error("Error saving privilege:", error);
      showSnackbar("Failed to save privilege", "error");
    } finally {
      dispatch({ type: "SET_IS_SUBMITTING", payload: false });
    }
  };

  const handleDelete = async () => {
    if (!selectedPrivilege) return;
    dispatch({ type: "SET_IS_SUBMITTING", payload: true });
    try {
      await deletePrivilege(selectedPrivilege.id);
      dispatch({ type: "SET_DELETE_DIALOG_OPEN", payload: false });
      dispatch({ type: "SET_SELECTED_PRIVILEGE", payload: null });
      mutate();
      showSnackbar("Privilege deleted successfully", "success");
    } catch (error) {
      console.error("Error deleting privilege:", error);
      showSnackbar("Failed to delete privilege", "error");
    } finally {
      dispatch({ type: "SET_IS_SUBMITTING", payload: false });
    }
  };

  const handleBulkDelete = async () => {
    dispatch({ type: "SET_IS_SUBMITTING", payload: true });
    try {
      await Promise.all(selectedIds.map((id) => deletePrivilege(id)));
      dispatch({ type: "SET_BULK_DELETE_DIALOG_OPEN", payload: false });
      dispatch({ type: "SET_SELECTED_IDS", payload: [] });
      mutate();
      showSnackbar(
        `${selectedIds.length} privilege(s) deleted successfully`,
        "success",
      );
    } catch (error) {
      console.error("Error deleting privileges:", error);
      showSnackbar("Failed to delete privileges", "error");
    } finally {
      dispatch({ type: "SET_IS_SUBMITTING", payload: false });
    }
  };

  const filteredPrivileges = useMemo(() => {
    return privileges.filter((privilege: Privilege) => {
      const searchLower = (filters.search || "").toLowerCase();
      const matchesSearch =
        !searchLower ||
        privilege.name.toLowerCase().includes(searchLower) ||
        privilege.category.toLowerCase().includes(searchLower) ||
        privilege.details.toLowerCase().includes(searchLower);

      const startDate = dayjs(privilege.start_date);
      const matchesMonth =
        filters.month === "all" || startDate.format("MM") === filters.month;
      const matchesYear =
        filters.year === "all" || startDate.format("YYYY") === filters.year;
      const status = getPrivilegeStatus(privilege);
      const matchesStatus =
        filters.status === "all" || filters.status === status;

      return matchesSearch && matchesMonth && matchesYear && matchesStatus;
    });
  }, [filters, privileges]);

  const pagedPrivileges = filteredPrivileges;

  const allSelected =
    pagedPrivileges.length > 0 &&
    pagedPrivileges.every((row: Privilege) => selectedIds.includes(row.id));
  const isIndeterminate =
    selectedIds.length > 0 && !allSelected && pagedPrivileges.length > 0;

  const handleSelectAll = (checked: boolean) => {
    if (checked) {
      dispatch({
        type: "SET_SELECTED_IDS",
        payload: pagedPrivileges.map((row: Privilege) => row.id),
      });
    } else {
      dispatch({ type: "SET_SELECTED_IDS", payload: [] });
    }
  };

  const handleSelectRow = (id: string) => {
    dispatch({
      type: "SET_SELECTED_IDS",
      payload: selectedIds.includes(id)
        ? selectedIds.filter((rowId: string) => rowId !== id)
        : [...selectedIds, id],
    });
  };

  const handleOpenActionMenu = (
    privilege: Privilege,
    event: React.MouseEvent<HTMLElement>,
  ) => {
    dispatch({
      type: "OPEN_ACTION_MENU",
      payload: { anchorEl: event.currentTarget, row: privilege },
    });
  };

  const handleCloseActionMenu = () => {
    dispatch({ type: "SET_ACTION_ANCHOR_EL", payload: null });
    dispatch({ type: "SET_ACTION_ROW", payload: null });
  };

  const handleActionEdit = () => {
    if (actionRow) handleEdit(actionRow);
    handleCloseActionMenu();
  };

  const handleActionDelete = () => {
    if (actionRow) handleDeleteClick(actionRow);
    handleCloseActionMenu();
  };

  const renderTable = () => (
    <>
      <TableContainer
        sx={{
          borderTop: `1px solid ${theme.vars.palette.divider}`,
          overflowX: "auto",
        }}
      >
        <Table sx={{ minWidth: 1700, "& td, & th": { whiteSpace: "nowrap" } }}>
          <TableHead>
            <TableRow
              sx={{
                "& .MuiTableCell-root": {
                  height: 52,
                  borderBottom: `1px solid ${theme.vars.palette.divider}`,
                  color: "#25272C",
                  fontSize: 12,
                  fontWeight: 600,
                  textTransform: "uppercase",
                  lineHeight: 1.4,
                  whiteSpace: "nowrap",
                },
              }}
            >
              <TableCell padding="checkbox" sx={{ width: 74 }}>
                <Checkbox
                  checked={allSelected}
                  indeterminate={isIndeterminate}
                  onChange={(event) => handleSelectAll(event.target.checked)}
                  sx={{ '&.Mui-checked': { color: '#1976D2' }, '&.MuiCheckbox-indeterminate': { color: '#1976D2' } }}
                />
              </TableCell>
              <TableCell align="center" sx={{ width: 80 }}>ACTIONS</TableCell>
              <TableCell align="center" sx={{ width: 86 }}>NO.</TableCell>
              <TableCell sx={{ width: 153 }}>PRIVILEGES ID</TableCell>
              <TableCell sx={{ width: 150 }}>CATEGORY</TableCell>
              <TableCell sx={{ width: 200 }}>PRIVILEGE NAME</TableCell>
              <TableCell sx={{ width: 200 }}>PRIVILEGE DETAILS</TableCell>
              <TableCell align="center" sx={{ width: 166 }}>STATUS</TableCell>
              <TableCell align="center" sx={{ width: 180 }}>POINTS REQUIRED</TableCell>
              <TableCell align="center" sx={{ width: 118 }}>START DATE</TableCell>
              <TableCell align="center" sx={{ width: 118 }}>END DATE</TableCell>
              <TableCell align="center" sx={{ width: 180 }}>CREATED DATE</TableCell>
            </TableRow>
          </TableHead>
          <TableBody>
            {pagedPrivileges.map((privilege: Privilege, index: number) => {
              const privilegeStatus = getPrivilegeStatus(privilege);
              const privilegeStatusChip = getStatusChip(privilegeStatus);

              return (
                <TableRow
                  key={privilege.id}
                  selected={selectedIds.includes(privilege.id)}
                  sx={{
                    "&.Mui-selected, &.Mui-selected:hover": {
                      backgroundColor: "#F9FAFB",
                    },
                    "& .MuiTableCell-root": {
                      height: 79,
                      borderBottom: `1px solid ${theme.vars.palette.divider}`,
                      fontSize: 12,
                      color: "#25272C",
                      whiteSpace: "nowrap",
                    },
                  }}
                >
                  <TableCell padding="checkbox" sx={{ width: 74 }}>
                    <Checkbox
                      checked={selectedIds.includes(privilege.id)}
                      onChange={() => handleSelectRow(privilege.id)}
                      sx={{ '&.Mui-checked': { color: '#1976D2' } }}
                    />
                  </TableCell>
                  <TableCell align="center" sx={{ width: 80 }}>
                    <IconButton
                      size="small"
                      onClick={(event) =>
                        handleOpenActionMenu(privilege, event)
                      }
                      sx={{
                        bgcolor:
                          actionRow?.id === privilege.id &&
                          Boolean(actionAnchorEl)
                            ? "#EDEEF1"
                            : "transparent",
                        borderRadius: "999px",
                        "&:hover": { bgcolor: "#EDEEF1" },
                      }}
                    >
                      <MoreVertOutlined fontSize="small" />
                    </IconButton>
                  </TableCell>
                  <TableCell align="center" sx={{ width: 86 }}>
                    {filters.page && filters.page_size
                      ? index + 1 + filters.page * filters.page_size
                      : index + 1}
                  </TableCell>
                  <TableCell sx={{ width: 153 }}>
                    {privilege.id}
                  </TableCell>
                  <TableCell sx={{ width: 150 }}>
                    {privilege.category}
                  </TableCell>
                  <TableCell sx={{ width: 200 }}>
                    {privilege.name}
                  </TableCell>
                  <TableCell sx={{ width: 200, maxWidth: 180, overflow: "hidden", textOverflow: "ellipsis" }}>
                    {privilege.details}
                  </TableCell>
                  <TableCell align="center" sx={{ width: 166 }}>
                    <Chip
                      size="small"
                      label={privilegeStatusChip.label}
                      sx={{
                        minWidth: 54,
                        height: 24,
                        fontSize: 11,
                        borderRadius: 1,
                        ...privilegeStatusChip.sx,
                      }}
                    />
                  </TableCell>
                  <TableCell align="center" sx={{ width: 180 }}>
                    {Number(privilege.points_required).toLocaleString()}
                  </TableCell>
                  <TableCell align="center" sx={{ width: 118 }}>
                    {dayjs(privilege.start_date).format("DD/MM/YYYY")}
                  </TableCell>
                  <TableCell align="center" sx={{ width: 118 }}>
                    {privilege.end_date
                      ? dayjs(privilege.end_date).format("DD/MM/YYYY")
                      : "-"}
                  </TableCell>
                  <TableCell align="center" sx={{ width: 180 }}>
                    {dayjs(privilege.created_at).format("DD/MM/YYYY")}
                  </TableCell>
                </TableRow>
              );
            })}
          </TableBody>
        </Table>
      </TableContainer>
      {filteredPrivileges.length > 0 && (
        <TablePagination
          rowsPerPageOptions={[10, 25, 50]}
          component="div"
          count={totalRecords}
          rowsPerPage={filters.page_size || 10}
          page={filters.page || 0}
          onPageChange={handlePageChange}
          onRowsPerPageChange={handlePageSizeChange}
        />
      )}
    </>
  );

  return (
    <Stack spacing={2}>
      <Paper sx={{ borderRadius: 1, overflow: "hidden" }}>
        <Box
          sx={{
            p: 2,
            display: "flex",
            justifyContent: "space-between",
            gap: 2,
            flexWrap: "wrap",
            alignItems: "center",
          }}
        >
          <Box
            sx={{
              display: "flex",
              alignItems: "center",
              gap: 2,
              flexWrap: "wrap",
            }}
          >
            <TextField
              size="small"
              placeholder="Search"
              value={filters.search}
              onChange={(e) => handleSearchChange(e.target.value)}
              sx={{ width: 180 }}
              slotProps={{
                input: {
                  endAdornment: (
                    <InputAdornment position="end">
                      <SearchSharp sx={{ color: "text.secondary" }} />
                    </InputAdornment>
                  ),
                },
              }}
            />
            <TextField
              select
              size="small"
              value={filters.month}
              onChange={(event) => handleMonthChange(event.target.value)}
              sx={{
                minWidth: 100,
                maxWidth: 180,
                "& .MuiSelect-select": {
                  color:
                    filters.month === "all" ? "text.secondary" : "text.primary",
                },
              }}
            >
              {monthOptions.map((option) => (
                <MenuItem key={option.value} value={option.value}>
                  {option.label}
                </MenuItem>
              ))}
            </TextField>
            <TextField
              select
              size="small"
              value={filters.year}
              onChange={(event) => handleYearChange(event.target.value)}
              sx={{ minWidth: 80, maxWidth: 140 }}
            >
              {yearOptions.map((option: { value: string; label: string }) => (
                <MenuItem key={option.value} value={option.value}>
                  {option.label}
                </MenuItem>
              ))}
            </TextField>
            <TextField
              select
              size="small"
              value={filters.status}
              onChange={(event) => handleStatusChange(event.target.value)}
              sx={{
                minWidth: 130,
                maxWidth: 180,
                "& .MuiInputBase-root": {
                  height: 40,
                },
                "& .MuiSelect-select": {
                  display: "flex",
                  alignItems: "center",
                  py: "0 !important",
                },
              }}
              slotProps={{
                select: {
                  renderValue: (selected: unknown) => {
                    const value = selected as string;
                    if (value === "all") {
                      return <Typography sx={{ fontSize: 14, color: "text.secondary" }}>All Status</Typography>;
                    }
                    const chipProps = getStatusChip(value as "active" | "draft" | "expired");
                    return (
                      <Chip
                        size="small"
                        label={chipProps.label}
                        sx={{
                          height: 24,
                          fontSize: 12,
                          borderRadius: 1,
                          fontWeight: 500,
                          ...chipProps.sx,
                        }}
                      />
                    );
                  },
                } as object,
              }}
            >
              {statusOptions.map((option) => (
                <MenuItem key={option.value} value={option.value}>
                  {option.value !== "all" ? (
                    <Chip
                      size="small"
                      label={option.label}
                      sx={{
                        height: 24,
                        fontSize: 12,
                        borderRadius: 1,
                        fontWeight: 500,
                        pointerEvents: "none",
                        ...getStatusChip(option.value as "active" | "draft" | "expired").sx,
                      }}
                    />
                  ) : (
                    option.label
                  )}
                </MenuItem>
              ))}
            </TextField>
          </Box>

          {selectedIds.length > 0 ? (
            <Button
              variant="outlined"
              startIcon={<DeleteSharp />}
              disabled={isSubmitting}
              onClick={() =>
                dispatch({ type: "SET_BULK_DELETE_DIALOG_OPEN", payload: true })
              }
              sx={{
                flexShrink: 0,
                height: 40,
                borderRadius: "96px",
                borderColor: "#BE2217",
                color: "#BE2217",
                fontWeight: 700,
                fontSize: 14,
                textTransform: "none",
                whiteSpace: "nowrap",
                "&:hover": {
                  borderColor: "#A31B12",
                  bgcolor: "rgba(190,34,23,0.05)",
                },
              }}
            >
              Delete ({selectedIds.length})
            </Button>
          ) : (
            <Button
              variant="contained"
              onClick={handleAddNew}
              startIcon={<AddRounded />}
              sx={{
                borderRadius: "999px",
                px: 3,
                py: 1,
                textTransform: "none",
                fontWeight: 700,
                backgroundColor: "#111111",
                boxShadow:
                  "0px 1px 5px 0px rgba(0,0,0,0.12), 0px 2px 2px 0px rgba(0,0,0,0.14), 0px 3px 1px -2px rgba(0,0,0,0.2)",
                "&:hover": {
                  backgroundColor: "#111111",
                },
              }}
            >
              Create Privilege
            </Button>
          )}
        </Box>

        {isLoading ? (
          <TableLoadingSkeleton columns={PRIVILEGES_SKELETON_COLUMNS} />
        ) : filteredPrivileges.length === 0 ? (
          <Box sx={{ borderTop: `1px solid ${theme.vars.palette.divider}` }}>
            <EmptyState
              variant="privileges"
              actionLabel="Add Privilege"
              onAction={handleAddNew}
            />
          </Box>
        ) : (
          renderTable()
        )}
      </Paper>

      <Menu
        anchorEl={actionAnchorEl}
        open={Boolean(actionAnchorEl)}
        onClose={handleCloseActionMenu}
        slotProps={{
          paper: {
            sx: {
              minWidth: 200,
              borderRadius: "8px",
              boxShadow:
                "0px 2px 1px -1px rgba(0,0,0,0.2), 0px 1px 1px 0px rgba(0,0,0,0.14), 0px 1px 3px 0px rgba(0,0,0,0.12)",
              overflow: "hidden",
            },
          },
          list: {
            sx: { p: 0 },
          },
        }}
      >
        <MenuItem
          onClick={handleActionEdit}
          sx={{
            minHeight: 40,
            py: 1,
            px: 3,
            gap: 1,
            fontSize: 16,
            fontWeight: 400,
            lineHeight: 1.4,
            color: "#25272C",
            "&:hover": { bgcolor: "#EDEEF1" },
          }}
        >
          <ListItemIcon sx={{ minWidth: 16, color: "#25272C" }}>
            <EditSharp sx={{ fontSize: 20 }} />
          </ListItemIcon>
          <ListItemText
            primary="Edit"
            slotProps={{ primary: { sx: { fontSize: 28 / 2, lineHeight: 1.4 } } }}
          />
        </MenuItem>
        <MenuItem
          onClick={handleActionDelete}
          sx={{
            minHeight: 40,
            py: 1,
            px: 3,
            gap: 1,
            fontSize: 16,
            fontWeight: 400,
            lineHeight: 1.4,
            color: "#25272C",
            "&:hover": { bgcolor: "#EDEEF1" },
          }}
        >
          <ListItemIcon sx={{ minWidth: 16, color: "#25272C" }}>
            <DeleteSharp sx={{ fontSize: 20 }} />
          </ListItemIcon>
          <ListItemText
            primary="Delete"
            slotProps={{ primary: { sx: { fontSize: 28 / 2, lineHeight: 1.4 } } }}
          />
        </MenuItem>
      </Menu>

      <CustomDialog
        open={dialogOpen}
        title={selectedPrivilege ? "Edit Privilege" : "Add New Privilege"}
        onClose={() => dispatch({ type: "SET_DIALOG_OPEN", payload: false })}
        maxWidth="md"
        content={
          <PrivilegeForm
            privilege={selectedPrivilege}
            onSubmit={handleFormSubmit}
            onCancel={() =>
              dispatch({ type: "SET_DIALOG_OPEN", payload: false })
            }
          />
        }
      />

      <CustomConfirmDialog
        open={deleteDialogOpen}
        title="Delete Privilege"
        onClose={() =>
          dispatch({ type: "SET_DELETE_DIALOG_OPEN", payload: false })
        }
        content={
          <Typography>
            Are you sure you want to delete{" "}
            <strong>{selectedPrivilege?.name}</strong>? This action cannot be
            undone.
          </Typography>
        }
        action={
          <Stack direction="row" spacing={2}>
            <Button
              variant="outlined"
              onClick={() =>
                dispatch({ type: "SET_DELETE_DIALOG_OPEN", payload: false })
              }
              disabled={isSubmitting}
            >
              Cancel
            </Button>
            <Button
              variant="contained"
              color="error"
              onClick={handleDelete}
              disabled={isSubmitting}
            >
              Delete
            </Button>
          </Stack>
        }
      />

      <CustomConfirmDialog
        open={bulkDeleteDialogOpen}
        title="Delete Privileges"
        onClose={() =>
          dispatch({ type: "SET_BULK_DELETE_DIALOG_OPEN", payload: false })
        }
        content={
          <Typography>
            Are you sure you want to delete{" "}
            <strong>{selectedIds.length} privilege(s)</strong>? This action
            cannot be undone.
          </Typography>
        }
        action={
          <Stack direction="row" spacing={2}>
            <Button
              variant="outlined"
              onClick={() =>
                dispatch({ type: "SET_BULK_DELETE_DIALOG_OPEN", payload: false })
              }
              disabled={isSubmitting}
            >
              Cancel
            </Button>
            <Button
              variant="contained"
              color="error"
              onClick={handleBulkDelete}
              disabled={isSubmitting}
            >
              Delete
            </Button>
          </Stack>
        }
      />
    </Stack>
  );
}
